import { supabase } from '../utils/supabase';

/**
 * Fetch session review games table data
 * Shows all games played in the session with players, ELOs, scores, and results
 * @param {string} sessionId - Session ID
 * @returns {Promise<Array>} - Array of game objects with team details and ELOs
 */
export const fetchSessionReviewGames = async (sessionId) => {
  try {
    // 1) Fetch all games for the session in one call
    const { data: games, error: gamesError } = await supabase
      .from('session_games')
      .select(`
        id,
        created_at,
        start_time,
        end_time,
        team_1_score,
        team_2_score,
        winning_team,
        game_create_method,
        game_types:game_type_id(
          id,
          name,
          is_doubles
        )
      `)
      .eq('session_id', sessionId)
      .order('start_time', { ascending: true });

    if (gamesError) throw gamesError;
    if (!games || games.length === 0) return [];

    const gameIds = games.map(g => g.id);

    // 2) Fetch all player-game relationships in one call
    const { data: playerGames, error: playerGamesError } = await supabase
      .from('players_session_games')
      .select(`
        session_game_id,
        player_id,
        team_number,
        pair_type,
        players:player_id(
          id,
          first_name,
          last_name,
          gender
        )
      `)
      .in('session_game_id', gameIds);

    if (playerGamesError) throw playerGamesError;

    // 3) Fetch all elo_history rows for these games in one call
    const { data: eloHistory, error: eloError } = await supabase
      .from('elo_history')
      .select('session_game_id, player_id, old_value, new_value, elo_change, pair_type')
      .in('session_game_id', gameIds);

    if (eloError) {
      console.error('Error fetching ELO history:', eloError);
    }

    // Build maps for quick lookups
    const playersByGameId = {};
    playerGames.forEach(pg => {
      if (!playersByGameId[pg.session_game_id]) {
        playersByGameId[pg.session_game_id] = [];
      }
      playersByGameId[pg.session_game_id].push(pg);
    });

    const eloByGameId = {};
    (eloHistory || []).forEach(eh => {
      if (!eloByGameId[eh.session_game_id]) {
        eloByGameId[eh.session_game_id] = [];
      }
      eloByGameId[eh.session_game_id].push(eh);
    });

    // Assemble game objects
    const gamesWithDetails = games.map((game, index) => {
      const gamePlayers = playersByGameId[game.id] || [];
      const gameElo = eloByGameId[game.id] || [];

      // Build ELO map keyed by player+pair_type
      const eloMap = {};
      gameElo.forEach(eh => {
        const key = `${eh.player_id}_${eh.pair_type}`;
        if (!eloMap[key]) eloMap[key] = [];
        eloMap[key].push(eh);
      });

      const team1Players = [];
      const team2Players = [];

      gamePlayers.forEach(gp => {
        const player = {
          id: gp.player_id,
          name: `${gp.players?.first_name || ''} ${gp.players?.last_name || ''}`.trim(),
          gender: gp.players?.gender,
          pair_type: gp.pair_type,
          team_number: gp.team_number
        };

        const eloKey = `${gp.player_id}_${gp.pair_type}`;
        const eloRecords = eloMap[eloKey] || [];
        if (eloRecords.length > 0) {
          const eloRecord = eloRecords[0];
          player.starting_elo = eloRecord.old_value;
          player.ending_elo = eloRecord.new_value;
          player.elo_change = eloRecord.elo_change;
        }

        if (gp.team_number === 1) {
          team1Players.push(player);
        } else {
          team2Players.push(player);
        }
      });

      const team1PlayersWithElo = team1Players.filter(p => p.starting_elo !== null && p.starting_elo !== undefined);
      const team2PlayersWithElo = team2Players.filter(p => p.starting_elo !== null && p.starting_elo !== undefined);

      const team1AvgElo = team1PlayersWithElo.length > 0
        ? Math.round(team1PlayersWithElo.reduce((sum, p) => sum + p.starting_elo, 0) / team1PlayersWithElo.length)
        : null;
      const team2AvgElo = team2PlayersWithElo.length > 0
        ? Math.round(team2PlayersWithElo.reduce((sum, p) => sum + p.starting_elo, 0) / team2PlayersWithElo.length)
        : null;

      const pairType = gamePlayers.length > 0 ? gamePlayers[0].pair_type : null;
      const isAltered = game.game_create_method && (
        game.game_create_method.includes('_altered') || 
        game.game_create_method === 'authoritative'
      );

      return {
        game_number: index + 1,
        game_id: game.id,
        game_created_at: game.created_at,
        start_time: game.start_time,
        end_time: game.end_time,
        game_type: game.game_types?.name || 'Unknown',
        is_doubles: game.game_types?.is_doubles || false,
        pair_type: pairType,
        team_1_score: game.team_1_score,
        team_2_score: game.team_2_score,
        winning_team: game.winning_team === 1 ? 'Team 1' : game.winning_team === 2 ? 'Team 2' : 'Draw',
        team_1_players: team1Players,
        team_2_players: team2Players,
        team_1_avg_starting_elo: team1AvgElo,
        team_2_avg_starting_elo: team2AvgElo,
        is_altered: isAltered,
        game_create_method: game.game_create_method
      };
    });

    return gamesWithDetails;
  } catch (error) {
    console.error('Error fetching session review games:', error);
    throw error;
  }
};

/**
 * Fetch session review player interactions table data
 * Shows how many times players played WITH and AGAINST each other
 * @param {string} sessionId - Session ID
 * @returns {Promise<Array>} - Array of player interaction objects
 */
export const fetchSessionReviewInteractions = async (sessionId) => {
  try {
    // First, get all games for this session with game_create_method
    const { data: games, error: gamesError } = await supabase
      .from('session_games')
      .select('id, game_create_method')
      .eq('session_id', sessionId);

    if (gamesError) throw gamesError;

    if (!games || games.length === 0) {
      return [];
    }

    const gameIds = games.map(g => g.id);
    
    // Create a map of game IDs to whether they were altered
    const alteredGamesMap = {};
    games.forEach(game => {
      const isAltered = game.game_create_method && (
        game.game_create_method.includes('_altered') || 
        game.game_create_method === 'authoritative'
      );
      alteredGamesMap[game.id] = isAltered;
    });

    // Fetch all player-game relationships for these games
    const { data: playerGames, error: playerGamesError } = await supabase
      .from('players_session_games')
      .select(`
        session_game_id,
        player_id,
        team_number,
        players:player_id(
          id,
          first_name,
          last_name,
          gender
        )
      `)
      .in('session_game_id', gameIds);

    if (playerGamesError) throw playerGamesError;

    // Build interaction map
    const interactionsMap = {};

    // Group by game
    const gamesByGameId = {};
    playerGames.forEach(pg => {
      if (!gamesByGameId[pg.session_game_id]) {
        gamesByGameId[pg.session_game_id] = [];
      }
      gamesByGameId[pg.session_game_id].push(pg);
    });

    // For each game, find all pairs
    Object.entries(gamesByGameId).forEach(([gameId, gamePlayers]) => {
      for (let i = 0; i < gamePlayers.length; i++) {
        for (let j = i + 1; j < gamePlayers.length; j++) {
          const p1 = gamePlayers[i];
          const p2 = gamePlayers[j];

          // Ensure consistent ordering (smaller ID first)
          const player1Id = p1.player_id < p2.player_id ? p1.player_id : p2.player_id;
          const player2Id = p1.player_id < p2.player_id ? p2.player_id : p1.player_id;

          const key = `${player1Id}_${player2Id}`;

          if (!interactionsMap[key]) {
            const player1 = p1.player_id < p2.player_id ? p1 : p2;
            const player2 = p1.player_id < p2.player_id ? p2 : p1;
            interactionsMap[key] = {
              player1_id: player1Id,
              player1_name: `${player1.players?.first_name || ''} ${player1.players?.last_name || ''}`.trim(),
              player1_gender: player1.players?.gender,
              player2_id: player2Id,
              player2_name: `${player2.players?.first_name || ''} ${player2.players?.last_name || ''}`.trim(),
              player2_gender: player2.players?.gender,
              games_played_together: 0,
              games_played_against: 0,
              total_interactions: 0,
              altered_games_count: 0 // Track how many altered games this interaction came from
            };
          }

          // Check if this game was altered
          const isAlteredGame = alteredGamesMap[gameId] || false;
          if (isAlteredGame) {
            interactionsMap[key].altered_games_count++;
          }

          // Determine interaction type
          if (p1.team_number === p2.team_number) {
            interactionsMap[key].games_played_together++;
          } else {
            interactionsMap[key].games_played_against++;
          }
          interactionsMap[key].total_interactions++;
        }
      }
    });

    // Convert map to array and sort
    const interactions = Object.values(interactionsMap);
    interactions.sort((a, b) => {
      if (b.total_interactions !== a.total_interactions) {
        return b.total_interactions - a.total_interactions;
      }
      if (b.games_played_together !== a.games_played_together) {
        return b.games_played_together - a.games_played_together;
      }
      return b.games_played_against - a.games_played_against;
    });

    return interactions;
  } catch (error) {
    console.error('Error fetching session review interactions:', error);
    throw error;
  }
};

/**
 * Fetch player interaction summary for session review
 * Shows per-player stats: unique players played with/against, and high interaction counts
 * @param {string} sessionId - Session ID
 * @returns {Promise<Array>} - Array of player summary objects
 */
export const fetchPlayerInteractionSummary = async (sessionId) => {
  try {
    // Get all games for this session with game_create_method
    const { data: games, error: gamesError } = await supabase
      .from('session_games')
      .select('id, game_create_method')
      .eq('session_id', sessionId);

    if (gamesError) throw gamesError;

    if (!games || games.length === 0) {
      return [];
    }

    const gameIds = games.map(g => g.id);
    
    // Create a map of game IDs to whether they were altered
    const alteredGamesMap = {};
    games.forEach(game => {
      const isAltered = game.game_create_method && (
        game.game_create_method.includes('_altered') || 
        game.game_create_method === 'authoritative'
      );
      alteredGamesMap[game.id] = isAltered;
    });

    // Fetch all player-game relationships
    const { data: playerGames, error: playerGamesError } = await supabase
      .from('players_session_games')
      .select(`
        session_game_id,
        player_id,
        team_number,
        players:player_id(
          id,
          first_name,
          last_name,
          gender
        )
      `)
      .in('session_game_id', gameIds);

    if (playerGamesError) throw playerGamesError;

    // Fetch player categories from session_players
    const { data: sessionPlayers, error: sessionPlayersError } = await supabase
      .from('session_players')
      .select('player_id, mixed_category, levels_category')
      .eq('session_id', sessionId);

    if (sessionPlayersError) throw sessionPlayersError;

    // Create maps of player_id to both category types
    const playerMixedCategoryMap = {};
    const playerLevelsCategoryMap = {};
    if (sessionPlayers) {
      sessionPlayers.forEach(sp => {
        playerMixedCategoryMap[sp.player_id] = sp.mixed_category || null;
        playerLevelsCategoryMap[sp.player_id] = sp.levels_category || null;
      });
    }

    // Build player stats map
    const playerStatsMap = {};

    // Group by game
    const gamesByGameId = {};
    playerGames.forEach(pg => {
      if (!gamesByGameId[pg.session_game_id]) {
        gamesByGameId[pg.session_game_id] = [];
      }
      gamesByGameId[pg.session_game_id].push(pg);
    });

    // Initialize player stats
    playerGames.forEach(pg => {
      const playerId = pg.player_id;
      if (!playerStatsMap[playerId]) {
        playerStatsMap[playerId] = {
          player_id: playerId,
          player_name: `${pg.players?.first_name || ''} ${pg.players?.last_name || ''}`.trim(),
          player_gender: pg.players?.gender,
          mixed_category: playerMixedCategoryMap[playerId] || null, // Mixed doubles category
          levels_category: playerLevelsCategoryMap[playerId] || null, // Levels category
          total_games: 0,
          played_with: new Set(), // Unique players played with
          played_against: new Set(), // Unique players played against
          interaction_counts: {}, // Count interactions with each other player
          altered_games_count: 0 // Count of altered games this player participated in
        };
      }
      playerStatsMap[playerId].total_games++;
      
      // Track if this game was altered
      if (alteredGamesMap[pg.session_game_id]) {
        playerStatsMap[playerId].altered_games_count++;
      }
    });

    // Process each game to build interaction data
    Object.values(gamesByGameId).forEach(gamePlayers => {
      for (let i = 0; i < gamePlayers.length; i++) {
        for (let j = i + 1; j < gamePlayers.length; j++) {
          const p1 = gamePlayers[i];
          const p2 = gamePlayers[j];

          const stats1 = playerStatsMap[p1.player_id];
          const stats2 = playerStatsMap[p2.player_id];

          if (!stats1 || !stats2) continue;

          // Track unique players
          if (p1.team_number === p2.team_number) {
            // Played together
            stats1.played_with.add(p2.player_id);
            stats2.played_with.add(p1.player_id);
          } else {
            // Played against
            stats1.played_against.add(p2.player_id);
            stats2.played_against.add(p1.player_id);
          }

          // Track interaction counts
          if (!stats1.interaction_counts[p2.player_id]) {
            stats1.interaction_counts[p2.player_id] = 0;
          }
          if (!stats2.interaction_counts[p1.player_id]) {
            stats2.interaction_counts[p1.player_id] = 0;
          }
          stats1.interaction_counts[p2.player_id]++;
          stats2.interaction_counts[p1.player_id]++;
        }
      }
    });

    // Convert to array and calculate high interaction counts
    const summaries = Object.values(playerStatsMap).map(stats => {
      // Count how many players this player had >=50% interaction with
      let highInteractionCount = 0;
      Object.entries(stats.interaction_counts).forEach(([otherPlayerId, interactionCount]) => {
        const interactionPercentage = (interactionCount / stats.total_games) * 100;
        if (interactionPercentage >= 50) {
          highInteractionCount++;
        }
      });

      return {
        player_id: stats.player_id,
        player_name: stats.player_name,
        player_gender: stats.player_gender,
        mixed_category: stats.mixed_category, // Mixed doubles category
        levels_category: stats.levels_category, // Levels category
        total_games: stats.total_games,
        unique_played_with: stats.played_with.size,
        unique_played_against: stats.played_against.size,
        high_interaction_count: highInteractionCount,
        altered_games_count: stats.altered_games_count
      };
    });

    // Sort by high interaction count (descending) to highlight potential poor experiences
    summaries.sort((a, b) => {
      if (b.high_interaction_count !== a.high_interaction_count) {
        return b.high_interaction_count - a.high_interaction_count;
      }
      // Then by total games (descending)
      return b.total_games - a.total_games;
    });

    return summaries;
  } catch (error) {
    console.error('Error fetching player interaction summary:', error);
    throw error;
  }
};
